import { CheckCircle2, FileText, Info, Loader2 } from 'lucide-react'

import { AIProvider } from './types'

interface Step3AIMemoryProps {
  aiProvider: AIProvider
  aiMemoryFile: File | null
  aiMemoryUploaded: boolean
  aiMemoryUploading: boolean
  setAiProvider: React.Dispatch<React.SetStateAction<AIProvider>>
  setAiMemoryFile: React.Dispatch<React.SetStateAction<File | null>>
  setAiMemoryFilePath: React.Dispatch<React.SetStateAction<string | null>>
  setAiMemoryUploaded: React.Dispatch<React.SetStateAction<boolean>>
  setError: React.Dispatch<React.SetStateAction<string | null>>
  uploadAiMemory: (file: File) => Promise<void>
}

export function Step3AIMemory({
  aiProvider,
  aiMemoryFile,
  aiMemoryUploaded,
  aiMemoryUploading,
  setAiProvider,
  setAiMemoryFile,
  setAiMemoryFilePath,
  setAiMemoryUploaded,
  setError,
  uploadAiMemory,
}: Step3AIMemoryProps) {
  return (
    <div className="space-y-6">
      <div>
        <div className="block text-sm font-medium text-text-secondary mb-3">
          Выберите AI-сервис, которым вы пользуетесь
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {(['chatgpt', 'claude', 'gemini', 'groq', 'none'] as const).map((provider) => (
            <button
              key={provider}
              type="button"
              onClick={() => {
                setAiProvider(provider)
                if (provider === 'none') {
                  setAiMemoryFile(null)
                  setAiMemoryFilePath(null)
                  setAiMemoryUploaded(false)
                }
              }}
              className={`p-4 border-2 rounded-lg text-center transition-all ${
                aiProvider === provider
                  ? 'border-green-600 bg-brand-primary-subtle'
                  : 'border-border-default hover:border-border-strong'
              }`}
            >
              <div className="text-2xl mb-2">
                {provider === 'chatgpt' && '🤖'}
                {provider === 'claude' && '🧠'}
                {provider === 'gemini' && '💎'}
                {provider === 'groq' && '⚡'}
                {provider === 'none' && '✖️'}
              </div>
              <div className="text-sm font-medium text-text-secondary capitalize">
                {provider === 'chatgpt' && 'ChatGPT'}
                {provider === 'claude' && 'Claude'}
                {provider === 'gemini' && 'Gemini'}
                {provider === 'groq' && 'Groq'}
                {provider === 'none' && 'Не использую'}
              </div>
            </button>
          ))}
        </div>
      </div>

      {aiProvider && aiProvider !== 'none' && (
        <div className="space-y-4">
          {/* Instructions */}
          <div className="bg-interactive-subtle border border-border-default rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-interactive flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-text-primary mb-2">
                  Как экспортировать память из {aiProvider === 'chatgpt' ? 'ChatGPT' : aiProvider === 'claude' ? 'Claude' : aiProvider === 'gemini' ? 'Gemini' : 'Groq'}
                </h3>
                <div className="text-sm text-text-secondary space-y-2">
                  {aiProvider === 'chatgpt' && (
                    <>
                      <p><strong>1.</strong> Откройте ChatGPT и перейдите в <strong>Settings → Data Controls → Export Data</strong></p>
                      <p><strong>2.</strong> Подтвердите экспорт. Вы получите ZIP-файл с историей чатов</p>
                      <p><strong>3.</strong> Распакуйте архив и найдите файл <code className="bg-blue-100 px-1 rounded">chat.html</code> или JSON файлы</p>
                      <p><strong>4.</strong> Загрузите экспортированный файл ниже</p>
                    </>
                  )}
                  {aiProvider === 'claude' && (
                    <>
                      <p><strong>1.</strong> Откройте Claude и перейдите в <strong>Settings → Privacy</strong></p>
                      <p><strong>2.</strong> Запросите экспорт данных. На ваш email придет ссылка для скачивания (действительна 24 часа)</p>
                      <p><strong>3.</strong> Или попросите Claude в чате: "Write out your memories of me verbatim, exactly as they appear in your memory"</p>
                      <p><strong>4.</strong> Скопируйте текст воспоминаний или загрузите экспортированный файл ниже</p>
                    </>
                  )}
                  {aiProvider === 'gemini' && (
                    <>
                      <p><strong>1.</strong> Откройте <a href="https://takeout.google.com" target="_blank" rel="noopener noreferrer" className="underline">Google Takeout</a></p>
                      <p><strong>2.</strong> Выберите <strong>Gemini Apps Activity</strong> и нажмите "Next"</p>
                      <p><strong>3.</strong> Выберите формат (JSON рекомендуется) и создайте экспорт</p>
                      <p><strong>4.</strong> После завершения скачайте архив и загрузите файл ниже</p>
                      <p className="text-xs text-blue-700 mt-2">⚠️ Важно: перед экспортом прокрутите все диалоги до начала, чтобы загрузить полную историю</p>
                    </>
                  )}
                  {aiProvider === 'groq' && (
                    <>
                      <p><strong>1.</strong> Groq не предоставляет встроенной функции экспорта памяти</p>
                      <p><strong>2.</strong> Если вы используете Groq через API, сохраните историю сообщений вручную</p>
                      <p><strong>3.</strong> Создайте текстовый файл или JSON с вашими диалогами</p>
                      <p><strong>4.</strong> Загрузите файл ниже</p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* File Upload */}
          <div>
            <label htmlFor="aiMemory" className="block text-sm font-medium text-text-secondary mb-2">
              Загрузите файл с памятью AI
            </label>
            
            {!aiMemoryUploaded ? (
              <div className="space-y-3">
                <div className="relative">
                  <input
                    id="aiMemory"
                    type="file"
                    accept=".json,.html,.txt,.md,.zip"
                    onChange={async (e) => {
                      const file = e.target.files?.[0]
                      if (file) {
                        // Validate file size (max 50MB for memory files)
                        if (file.size > 50 * 1024 * 1024) {
                          setError('Размер файла должен быть меньше 50MB')
                          return
                        }
                        setAiMemoryFile(file)
                        setError(null)
                        await uploadAiMemory(file)
                      }
                    }}
                    disabled={aiMemoryUploading}
                    className="block w-full text-sm text-text-muted file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-brand-primary-subtle file:text-brand-primary hover:file:bg-brand-primary-subtle disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
                
                {aiMemoryUploading && (
                  <div className="flex items-center gap-2 px-4 py-3 bg-interactive-subtle border border-border-default rounded-lg">
                    <Loader2 className="w-5 h-5 text-interactive animate-spin" />
                    <span className="text-sm font-medium text-blue-700">Загрузка файла памяти...</span>
                  </div>
                )}
                
                <p className="text-sm text-text-muted">
                  Поддерживаемые форматы: JSON, HTML, TXT, MD, ZIP (до 50MB)
                </p>
              </div>
            ) : (
              <div className="mt-2 p-4 bg-brand-primary-subtle border-2 border-brand-primary/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <CheckCircle2 className="w-6 h-6 text-brand-primary" strokeWidth={2.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="w-4 h-4 text-text-muted flex-shrink-0" />
                      <span className="text-sm font-medium text-text-primary truncate">
                        {aiMemoryFile?.name || 'Файл памяти загружен'}
                      </span>
                    </div>
                    <p className="text-xs text-brand-primary font-medium">
                      Файл памяти успешно загружен
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setAiMemoryUploaded(false)
                      setAiMemoryFilePath(null)
                      setAiMemoryFile(null)
                      const fileInput = document.getElementById('aiMemory') as HTMLInputElement
                      if (fileInput) {
                        fileInput.value = ''
                      }
                    }}
                    className="flex-shrink-0 text-text-muted hover:text-text-primary transition-colors"
                    title="Удалить файл"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {aiProvider === 'none' && (
        <div className="bg-surface-muted border border-border-default rounded-lg p-4 text-sm text-text-secondary">
          Вы можете пропустить этот шаг и загрузить память AI позже в настройках.
        </div>
      )}
    </div>
  )
}
